import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import {
  Visibility,
  Download,
  PictureAsPdf,
  Image,
  Description,
} from '@mui/icons-material';
import { ProjectDocument, getDocumentViewUrl, isImageFile, isPdfFile } from '../../services/projectsAPI';

interface DocumentPreviewProps {
  document: ProjectDocument;
  projectId: number;
  onDelete?: () => void;
}

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  document,
  projectId,
  onDelete,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const documentUrl = getDocumentViewUrl(projectId, document.id);

  const getFileIcon = () => {
    if (isImageFile(document.original_filename)) {
      return <Image color="primary" />;
    } else if (isPdfFile(document.original_filename)) {
      return <PictureAsPdf color="error" />;
    } else {
      return <Description color="action" />;
    }
  };

  const getFileType = () => {
    if (isImageFile(document.original_filename)) {
      return 'Bild';
    } else if (isPdfFile(document.original_filename)) {
      return 'PDF';
    } else {
      return 'Dokument';
    }
  };

  const handleView = () => {
    if (isImageFile(document.original_filename)) {
      setPreviewOpen(true);
    } else {
      // Für PDFs und andere Dateien öffne in neuem Tab
      window.open(documentUrl, '_blank');
    }
  };

  const handleDownload = () => {
    const link = window.document.createElement('a');
    link.href = documentUrl;
    link.download = document.original_filename;
    link.target = '_blank';
    link.click();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {getFileIcon()}
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              {document.original_filename}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip 
                label={getFileType()} 
                size="small" 
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                {(document.file_size / 1024).toFixed(1)} KB
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleView}
            title="Anzeigen"
            color="primary"
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleDownload}
            title="Herunterladen"
            color="secondary"
          >
            <Download />
          </IconButton>
          {onDelete && (
            <Button
              size="small"
              onClick={onDelete}
              color="error"
              variant="text"
            >
              Löschen
            </Button>
          )}
        </Box>
      </Box>

      {/* Bild-Vorschau Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Image />
            {document.original_filename}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={documentUrl}
              alt={document.original_filename}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownload} startIcon={<Download />}>
            Herunterladen
          </Button>
          <Button onClick={() => setPreviewOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 